import { useEffect, useState } from 'react';
import { Duck } from '../components/Duck';
import type { MascotName } from '../theme/ThemeProvider';

const LINES = [
  'Okay, so the function takes an array...',
  '...and it loops through it...',
  "Wait. I'm mutating the array INSIDE the loop.",
  "Oh. That's the bug. Thanks, duck.",
];

export function Explainer({ mascot }: { mascot: MascotName }): JSX.Element {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const msg = LINES[idx];
    if (msg === undefined) return;
    if (typed.length < msg.length) {
      const t = setTimeout(() => setTyped(msg.slice(0, typed.length + 1)), 38);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setTyped('');
      setIdx((i) => (i + 1) % LINES.length);
    }, 1800);
    return () => clearTimeout(t);
  }, [typed, idx]);

  return (
    <section className="border-t-2 border-ink bg-bg px-8 pt-[88px] pb-[72px]">
      <div className="mx-auto max-w-[1280px]">
        {/* Move 1 — origin story */}
        <div className="grid items-center gap-14" style={{ gridTemplateColumns: '1.35fr 1fr' }}>
          <div>
            <div className="mb-4 flex items-center gap-2.5">
              <span className="h-2 w-2 rounded-full bg-beak" />
              <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-soft">
                FIRST · WHAT'S A RUBBER DUCK?
              </span>
            </div>
            <h2
              className="m-0 font-display font-extrabold tracking-[-0.03em] text-ink"
              style={{ fontSize: 'clamp(36px, 4.6vw, 64px)', lineHeight: 0.98, textWrap: 'balance' as never }}
            >
              Engineers have been debugging with{' '}
              <span className="bg-duck px-[0.12em]" style={{ boxShadow: '3px 3px 0 rgb(var(--color-ink))' }}>
                ducks
              </span>{' '}
              for decades.
            </h2>
            <p className="mt-5 max-w-[580px] text-lg leading-snug text-ink-soft">
              <strong className="text-ink">Rubber duck debugging</strong> is a real practice, named in the 1999 book{' '}
              <em className="font-serif">The Pragmatic Programmer</em>. Stuck on a bug? You explain your code, line by
              line, to a small yellow object on your desk. The act of articulating the problem out loud — to something
              that can't talk back — is how you find the answer.
            </p>
            <p className="mt-3.5 max-w-[580px] text-lg leading-snug text-ink-soft">
              We're flipping it. <strong className="text-ink">This time the duck talks back.</strong> So do frontier AI
              agents. So do five working engineers who actually ship. We're debugging the industry, out loud, in public.
            </p>
          </div>

          {/* Live "duck listening" panel */}
          <div
            className="relative rounded-[18px] border-2 border-ink bg-paper p-7"
            style={{ boxShadow: '6px 6px 0 rgb(var(--color-ink))', minHeight: 280 }}
          >
            <div className="absolute font-mono text-[11px] tracking-[0.1em] text-ink-soft" style={{ top: 14, left: 18 }}>
              debug.log · live
            </div>
            <div className="absolute flex gap-1.5" style={{ top: 14, right: 18 }}>
              {['#dc2626', 'rgb(var(--color-duck))', '#16a34a'].map((c, i) => (
                <span
                  key={i}
                  className="h-2.5 w-2.5 rounded-full border border-ink"
                  style={{ background: c }}
                />
              ))}
            </div>

            {/* Speech bubble */}
            <div
              className="mt-9 inline-block max-w-[92%] border-2 border-ink bg-bg px-[18px] py-3.5 font-mono text-sm leading-snug text-ink"
              style={{ borderRadius: '14px 14px 14px 4px', minHeight: 56 }}
            >
              {typed}
              <span
                className="ml-0.5 inline-block h-4 w-2 bg-beak align-middle"
                style={{ animation: 'pulse 1.1s steps(2) infinite' }}
              />
            </div>

            {/* Duck */}
            <div className="mt-4 flex items-end justify-end gap-2.5">
              <div className="pb-5 font-serif text-lg italic text-ink-soft">
                …<span className="text-ink">listening</span>.
              </div>
              <Duck variant={mascot} size={140} />
            </div>
          </div>
        </div>

        {/* Move 2 — why */}
        <div
          className="relative mt-[72px] overflow-hidden rounded-[20px] border-2 border-ink bg-ink px-11 py-10 text-bg"
          style={{ boxShadow: '6px 6px 0 rgb(var(--color-duck))' }}
        >
          <div aria-hidden className="absolute opacity-10" style={{ top: -50, right: -40 }}>
            <Duck variant={mascot} size={260} />
          </div>
          <div className="relative max-w-[920px]">
            <div className="mb-4 flex items-center gap-2.5">
              <span className="h-2 w-2 rounded-full bg-duck" />
              <span className="font-mono text-xs uppercase tracking-[0.16em] text-duck">SECOND · WHY WE BUILT THIS</span>
            </div>
            <h3
              className="m-0 font-display font-extrabold tracking-[-0.02em]"
              style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.05, textWrap: 'balance' as never }}
            >
              We want{' '}
              <span className="font-serif font-normal italic text-duck">open, honest</span> conversations between real
              practitioners using AI to ship software.
            </h3>
            <p className="mt-5 max-w-[760px] text-lg leading-snug text-bg/80">
              Both sides of the equation. The engineers who think this is the biggest leverage shift of their careers,
              and the ones who think most of it is accounting tricks. The roles are changing — or are they? — and the
              only people qualified to argue about it are the ones doing the work.
            </p>
            <p className="mt-3.5 max-w-[760px] text-lg leading-snug text-bg/80">
              No vendor pitches. No keynote energy. No moderator who hasn't shipped in five years. Just practitioners,
              the bots they use (and argue with), and an audience of working engineers willing to be wrong out loud.
            </p>
          </div>
        </div>

        {/* Move 3 — format spec */}
        <div className="mt-[72px]">
          <div className="mb-4 flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full bg-beak" />
            <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-soft">THIRD · THE FORMAT</span>
          </div>
          <h3
            className="m-0 mb-7 max-w-[920px] font-display font-extrabold tracking-[-0.02em] text-ink"
            style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.05, textWrap: 'balance' as never }}
          >
            Real practitioners + AI agents, debating live. Weekly-ish. Free.
          </h3>

          <div className="grid grid-cols-4 gap-4">
            <FormatCard k="WHO" v="Humans + bots" body="Five working engineers. Two AI agents that listen, raise their hand, and speak for themselves." />
            <FormatCard k="WHEN" v="Weekly · monthly" body="A new motion every week or two. Topics surface from working engineers, not a content calendar." accent />
            <FormatCard k="WHERE" v="Virtual" body="Streamed to your nearest tub. No travel, no badge line, no awkward hotel coffee." />
            <FormatCard k="HOW MUCH" v="Free" body="The cost is not being in the room while the field gets argued out." />
          </div>

          <div className="mx-auto mt-4 max-w-[760px] text-center font-serif text-[17px] italic text-ink-soft">
            "The cost is not having the knowledge we're about to share, and not hearing the opinions of others."
          </div>
        </div>
      </div>
    </section>
  );
}

function FormatCard({ k, v, body, accent }: { k: string; v: string; body: string; accent?: boolean }): JSX.Element {
  return (
    <div
      className={`flex min-h-[180px] flex-col gap-2.5 rounded-[14px] border-2 border-ink ${accent ? 'bg-duck' : 'bg-paper'} px-[22px] pt-5 pb-[22px]`}
      style={{ boxShadow: '4px 4px 0 rgb(var(--color-ink))' }}
    >
      <span className="font-mono text-[11px] font-bold tracking-[0.14em] text-ink-soft">{k}</span>
      <span className="font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em] text-ink">{v}</span>
      <span className={`text-sm leading-snug ${accent ? 'text-ink' : 'text-ink-soft'}`}>{body}</span>
    </div>
  );
}
